import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faStar,
  faLink,
  faCheck,
  faClipboard,
  faMicrochip,
  faArrowUp,
  faArrowDown,
  faComment,
  faLayerGroup,
  faBook,
  faLightbulb,
  faChessBoard,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Color } from '../types/Color';
import GameContext from '../context/GameContext';
import AppContext from '../context/AppContext';
import AnnotationsContext from '../context/AnnotationsContext';
import CommentTreeView from './CommentTreeView';
import MiniChessboard from './MiniChessboard';
import GamesListItemEvaluationBar from './GamesListItemEvaluationBar';
import GamesListItemControls from './GamesListItemControls';
import GamesMoveAlternatives from './GamesMoveAlternatives';
import GoodMovesGauge from './GoodMovesGauge';
import { useMoveInsights } from './useMoveInsights';
import { usePractice } from './PracticeProvider';
import { useCorrections } from './CorrectionsProvider';
import { moveTypeClass } from '../utils/ChessTypes';
import { patternLabel } from './patternThemes';
import TagPicker from './TagPicker';
import { analytics } from '../utils/analytics';

// Wording for a tactical-motif tag (TacticalPatterns). 'future' = the move let the opponent play it;
// 'past' = the player should have played it. Unknown tags fall back gracefully.
const MOTIF_NOUNS: Record<string, string> = { fork: 'fork' };
// The learning point: names the motif behind the mistake, future before past (walking into a tactic
// is the headline). `firstMoveChip` is the best line's first move, rendered as a tappable chip.
function motifBanner(
  future: string | null | undefined,
  past: string | null | undefined,
  firstMoveChip: React.ReactNode,
  tone: string,
) {
  const when: 'past' | 'future' | null = future ? 'future' : past ? 'past' : null;
  const tag = future ?? past;
  if (when == null || !tag) return null;
  const noun = MOTIF_NOUNS[tag] ?? tag.replace(/-/g, ' ');
  const label = when === 'future' ? `Missed a future ${noun}` : `Missed a ${noun} you had`;
  const sub =
    when === 'future'
      ? `Your move let your opponent play a ${noun}.`
      : `You had a ${noun} here and didn't play it.`;
  // Accent follows the move's severity, matching the move-type label.
  return (
    <div
      className={`cm-cp-motif-banner cm-cp-motif-banner--${when}${tone ? ` cm-cp-motif-banner--${tone}` : ''}`}
    >
      <FontAwesomeIcon icon={faLightbulb} className="cm-cp-motif-banner-icon" aria-hidden="true" />
      <div className="cm-cp-motif-banner-text">
        <span className="cm-cp-motif-banner-label">{label}</span>
        <span className="cm-cp-motif-banner-sub">
          {sub}
          {firstMoveChip}
        </span>
      </div>
    </div>
  );
}

// The Move panel, tracking the board's move (GameContext.currentMove):
//  - 'docked' (default): the right-hand column on desktop: readout, alternatives, PGN, comments.
//  - 'inline': part of the mobile game view (GameMobileView), under the real board, ordered swing ->
//    comments -> alternatives -> PGN.
export default function CommentPanel(
  props: { variant?: 'docked' | 'inline' } = {},
): React.ReactNode {
  const variant = props.variant ?? 'docked';
  const inline = variant === 'inline';

  const { game, currentMove, orientation, chessviewerRef } = useContext(GameContext);
  const { isMobile, isAuthenticated } = useContext(AppContext);
  const { comments, favorites, toggleFavorite, closePanel } = useContext(AnnotationsContext);
  const { winSwing, mate, parentBestMove, bestLine, bestLineFromParent, moveTypeLabel, EvalIcon, viewerName } =
    useMoveInsights();
  const practice = usePractice();
  const { activeMistakes } = useCorrections();

  const movePgn = currentMove?.Pgn ?? null;
  const [copied, setCopied] = useState(false);
  const [pgnCopied, setPgnCopied] = useState(false);
  const [starToast, setStarToast] = useState('');
  // The best-line ply the board sits on (highlighted chip), or null when not exploring this line.
  // Mirrored in a ref so the once-subscribed handlers read the latest line.
  const [activeBestPly, setActiveBestPly] = useState<number | null>(null);
  // Whether the board is exploring; drives the exit "X" at the end of the line.
  const [exploring, setExploring] = useState(false);
  const bestLineRef = useRef(bestLine);
  bestLineRef.current = bestLine;
  const bestLineSubscribed = useRef(false);

  useEffect(() => {
    setPgnCopied(false);
    setActiveBestPly(null);
  }, [movePgn]);

  // While exploring this best line the board's history is the line's prefix, so the last move's index is
  // the active ply. Any divergence clears it.
  const syncActivePly = () => {
    const viewer = chessviewerRef.current;
    const bl = bestLineRef.current;
    if (viewer == null || bl == null || !viewer.isExploring()) {
      setActiveBestPly(null);
      return;
    }
    const played = viewer.chess.history({ verbose: true });
    for (let i = 0; i < played.length; i++) {
      const want = bl.uci[i];
      if (want == null || `${played[i].from}${played[i].to}` !== want.slice(0, 4)) {
        setActiveBestPly(null);
        return;
      }
    }
    setActiveBestPly(played.length > 0 ? played.length - 1 : null);
  };

  // Subscribe once; handlers read refs only, so the first closure stays correct.
  useEffect(() => {
    const viewer = chessviewerRef.current;
    if (viewer == null || bestLineSubscribed.current) {
      return;
    }
    bestLineSubscribed.current = true;
    viewer.onExplore(syncActivePly);
    viewer.onPlayback(() => setActiveBestPly(null));
    viewer.onExploringChange((isExploring) => {
      setExploring(isExploring);
      if (!isExploring) {
        setActiveBestPly(null);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const scoped = useMemo(
    () => comments.filter((c) => (c.MovePgn ?? null) === movePgn),
    [comments, movePgn],
  );

  const isFav = movePgn != null && favorites.has(movePgn);

  const flashStar = (msg: string) => {
    setStarToast(msg);
    window.setTimeout(() => setStarToast(''), 1600);
  };

  const onToggleFav = () => {
    if (movePgn == null) {
      return;
    }
    // Optimistic in AnnotationsContext; feedback doesn't wait for the server.
    flashStar(isFav ? 'Move unstarred' : 'Move marked as starred');
    toggleFavorite(movePgn);
  };

  const share = () => {
    if (currentMove == null) {
      return;
    }
    // Filter-safe deep link: the move as number+colour, resolved against the game on load.
    const ref = `${currentMove.Number}${currentMove.Color === Color.White ? 'w' : 'b'}`;
    const url = `${window.location.origin}${window.location.pathname}#/?gameId=${game.Id}&move=${ref}`;
    // Confirm immediately; the clipboard write is fire-and-forget so a rejected promise can't swallow it.
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
    navigator.clipboard?.writeText(url).catch(() => {});
  };

  const copyPgn = () => {
    if (currentMove == null) {
      return;
    }
    setPgnCopied(true);
    window.setTimeout(() => setPgnCopied(false), 1500);
    navigator.clipboard?.writeText(currentMove.Pgn).catch(() => {});
  };

  // Tap a best-line move to play the line up to it on the board, in explore mode.
  const playBestLineTo = (index: number) => {
    const viewer = chessviewerRef.current;
    if (viewer == null || bestLine == null) {
      return;
    }
    viewer.exploreLine(bestLine.baseFen, bestLine.uci, index);
    setActiveBestPly(index);
  };

  // Leave explore mode, like the "X" in the board controls: back to the game move.
  const exitExplore = () => {
    chessviewerRef.current?.setDraggable(false);
  };

  // Horizontal swipe over the docked board steps moves: right = previous, left = next.
  const boardTouch = useRef<{ x: number; y: number } | null>(null);
  const onBoardTouchStart = (e: React.TouchEvent) => {
    boardTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onBoardTouchEnd = (e: React.TouchEvent) => {
    const start = boardTouch.current;
    boardTouch.current = null;
    if (start == null) {
      return;
    }
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      chessviewerRef.current?.go(dx > 0 ? 'previous' : 'next');
    }
  };

  // Swipe the header down to dismiss the mobile bottom sheet (sheet variant only).
  const touchStartY = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current == null) {
      return;
    }
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (isMobile && dy > 60) {
      closePanel();
    }
  };

  const title =
    currentMove != null
      ? `${currentMove.Number}.${currentMove.Color === Color.White ? '' : '..'} ${currentMove.NewPosition}`
      : 'Comments';

  // The move readout on the header line: icon + label + win-chance swing + the engine's best move, e.g.
  // "28… Ke8 ⛭ Blunder ↓14% Best Qd7".
  const tone = moveTypeClass(currentMove?.MoveType);
  const moveHead =
    currentMove != null ? (
      <span className="cm-cp-move-head">
        <span className="cm-cp-move-label">{title}</span>
        {moveTypeLabel ? (
          <span className={`cm-cp-move-type ${tone}`}>
            <EvalIcon className="cm-cp-move-icon" />
            {moveTypeLabel}
          </span>
        ) : null}
        {winSwing != null ? (
          <span
            className={`cm-cp-move-swing ${winSwing < 0 ? 'down' : winSwing > 0 ? 'up' : 'flat'}`}
            title={
              winSwing === 0
                ? `${viewerName} win chance unchanged`
                : `${viewerName} ${winSwing < 0 ? 'lost' : 'gained'} ${Math.abs(winSwing)}% win chance`
            }
          >
            {winSwing !== 0 ? (
              <FontAwesomeIcon icon={winSwing < 0 ? faArrowDown : faArrowUp} className="me-1" />
            ) : null}
            {Math.abs(winSwing)}%
          </span>
        ) : null}
        {/* Forced mate from the viewer's side: "M3" or "-M3". The win chance is already 100/0. */}
        {mate != null ? (
          <span
            className={`cm-cp-move-mate ${mate.startsWith('-') ? 'down' : 'up'}`}
            title={
              mate.startsWith('-')
                ? `${viewerName} is getting mated in ${mate.replace('-M', '')}`
                : `${viewerName} has forced mate in ${mate.replace('M', '')}`
            }
          >
            {mate}
          </span>
        ) : null}
      </span>
    ) : (
      <span className="cm-cp-move-head">{title}</span>
    );

  // Tier-0 engine note about the move. When its theme has a pattern page, link to it: from this game to
  // "you keep doing this" across every game.
  const patternTheme = currentMove?.CommentaryTheme ?? null;
  const patternName = patternLabel(patternTheme);
  // The engine's commentary once evaluated, otherwise the book move's opening name (currentMove.Comment,
  // available at load). A book fallback shows the book glyph.
  const noteText = currentMove?.Commentary ?? currentMove?.Comment ?? null;
  const noteIsBook = !currentMove?.Commentary && currentMove?.Comment != null;
  const commentaryBlock = noteText ? (
    <div className="cm-cp-commentary">
      <FontAwesomeIcon
        icon={noteIsBook ? faBook : faMicrochip}
        className="cm-cp-commentary-icon"
        aria-hidden="true"
      />
      <div className="cm-cp-commentary-body">
        <span>{noteText}</span>{' '}
        {patternName != null && patternTheme != null ? (
          <a
            className="cm-cp-pattern"
            href={`/patterns/${patternTheme}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`${patternName}: see this pattern across all games`}
            onClick={() =>
              analytics.track('select_pattern', { pattern: patternTheme, ui_source: 'move_panel' })
            }
          >
            <FontAwesomeIcon icon={faLayerGroup} aria-hidden="true" /> See{' '}
            <span className="cm-cp-pattern-name">{patternName}</span> across all games &rarr;
          </a>
        ) : null}
      </div>
    </div>
  ) : null;

  // Both resolved server-side:
  //   * RiskNote: the verdict on this move, one sentence, only when it went wrong.
  //   * GoodMoveCount / LegalMoveCount: the position it was chosen from, for the gauge.
  // A count rather than a score: "14 of 44 moves here were good" is something a player can carry.
  const riskNote = currentMove?.RiskNote ?? null;
  const legalMoves = currentMove?.LegalMoveCount ?? null;
  const goodMoves = currentMove?.GoodMoveCount ?? null;
  // The engine ranks a fixed number of candidates, so in a comfortable position the count stops at that
  // ceiling. All we know then is "at least this many", which is what the gauge says instead.
  const goodIsCeiling = currentMove?.DifficultySaturated === true;

  const riskBlock = riskNote ? (
    <div className="cm-cp-risk">
      <FontAwesomeIcon icon={faTriangleExclamation} className="cm-cp-risk-icon" aria-hidden="true" />
      <div className="cm-cp-risk-body">{riskNote}</div>
    </div>
  ) : null;

  // On every move, not only mistakes: a right move in a wide-open position differs from one where only
  // two moves worked. The gauge is shared with the repair session.
  const difficultyBlock = (
    <GoodMovesGauge
      goodMoves={goodMoves}
      legalMoves={legalMoves}
      saturated={goodIsCeiling}
    />
  );

  // Mobile only: repair just this move, for the viewer's own unsolved repairable mistakes (those in
  // activeMistakes). Solving it leaves practice. Hidden while already practising.
  const repairableMistake =
    isMobile && !practice.active && currentMove != null
      ? activeMistakes.find((m) => m.MoveId === currentMove.MoveId)
      : undefined;
  // Only while unsolved.
  const repairMoveBtn = repairableMistake != null && !repairableMistake.effectiveSolved ? (
    <button
      type="button"
      className="cm-cp-repair-move"
      onClick={() => practice.repairOne(currentMove!.MoveId)}
    >
      <FontAwesomeIcon icon={faChessBoard} className="me-2" />
      Repair this move
    </button>
  ) : null;

  // The motif banner, at the top of the analysis block, with the best line's first move as a chip.
  const firstBestPly = bestLine?.plies[0] ?? null;
  const motifFirstMoveChip = firstBestPly ? (
    <button
      type="button"
      className={`cm-cp-bestline-move cm-mono${firstBestPly.index === activeBestPly ? ' active' : ''}`}
      onClick={() => playBestLineTo(firstBestPly.index)}
      aria-pressed={firstBestPly.index === activeBestPly}
      title="Play this move on the board"
    >
      {firstBestPly.san}
    </button>
  ) : null;
  const motifBannerEl = motifBanner(
    currentMove?.PatternFuture,
    currentMove?.PatternPast,
    motifFirstMoveChip,
    tone,
  );

  // The player's own tags on this move, keyed by the move's Pgn.
  const moveTagBlock =
    currentMove != null ? (
      <TagPicker scope="Move" pgn={currentMove.Pgn} label="Your private read on this move" />
    ) : null;

  // The engine's best line (error-family moves, see useMoveInsights). Each move is tappable.
  const bestLineBlock = bestLine ? (
    <div className="cm-cp-bestline">
      <span className="cm-cp-bestline-label">{bestLineFromParent ? 'Instead:' : 'From here:'}</span>
      {bestLine.plies.map((ply) => (
        <React.Fragment key={ply.index}>
          {ply.numberLabel ? (
            <span className="cm-cp-bestline-num cm-mono">{ply.numberLabel}</span>
          ) : null}
          <button
            type="button"
            className={`cm-cp-bestline-move cm-mono${ply.index === activeBestPly ? ' active' : ''}`}
            onClick={() => playBestLineTo(ply.index)}
            aria-pressed={ply.index === activeBestPly}
            title="Play this line on the board"
          >
            {ply.san}
          </button>
        </React.Fragment>
      ))}
      {exploring ? (
        <button
          type="button"
          className="cm-cp-bestline-exit"
          onClick={exitExplore}
          title="Exit explore mode"
          aria-label="Exit explore mode"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      ) : null}
    </div>
  ) : null;

  // Two dropdowns to branch off the current move: its siblings (other moves played instead) and its
  // continuations (moves played after it). HasAlternatives pre-gates them without a fetch: this move's
  // flag covers siblings, the next move's flag covers continuations. The flag is a global count and can
  // over-report in a group, so each dropdown fetches its scoped list and hides when it adds nothing.
  // The container hides via :empty when both opt out.
  const moveName = currentMove?.NewPosition ?? 'this move';
  // Flatten the White/Black pairs to find the move after this one. curIdx is -1 on an explored line,
  // so continuations stay hidden.
  const flatMoves = game.Moves.flatMap((p) => (p.Black != null ? [p.White, p.Black] : [p.White]));
  const curIdx = currentMove != null ? flatMoves.findIndex((m) => m.Pgn === currentMove.Pgn) : -1;
  const nextMove = curIdx >= 0 ? flatMoves[curIdx + 1] : undefined;
  const hasSiblings = currentMove?.HasAlternatives === true;
  const hasContinuations = nextMove?.HasAlternatives === true;
  const alternativesBlock =
    currentMove != null && (hasSiblings || hasContinuations) ? (
      <div className="cm-cp-alternatives" key={currentMove.Pgn}>
        {hasSiblings ? (
          <GamesMoveAlternatives
            move={currentMove}
            mode="siblings"
            label={`Other moves besides ${moveName}`}
            currentPgn={currentMove.Pgn}
          />
        ) : null}
        {hasContinuations && nextMove != null ? (
          <GamesMoveAlternatives
            move={currentMove}
            mode="continuations"
            label={`Other moves after ${moveName}`}
            currentPgn={nextMove.Pgn}
          />
        ) : null}
      </div>
    ) : null;

  // Per-move actions on the header line: Star · Copy link · Copy PGN (+ sheet close on mobile).
  const moveActions = (
    <span className="cm-comment-panel-actions">
      {isAuthenticated ? (
        <span className="cm-cp-star-wrap">
          <button
            type="button"
            className={`cm-cp-action cm-cp-star${isFav ? ' active' : ''}`}
            onClick={onToggleFav}
            disabled={movePgn == null}
            aria-pressed={isFav}
            title={isFav ? 'Unstar this move' : 'Star (favorite move)'}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
          {starToast ? <span className="cm-cp-toast">{starToast}</span> : null}
        </span>
      ) : null}
      <button
        type="button"
        className="cm-cp-action"
        onClick={share}
        disabled={currentMove == null}
        title="Copy a link to this move"
      >
        <FontAwesomeIcon icon={copied ? faCheck : faLink} />
      </button>
      <button
        type="button"
        className="cm-cp-action"
        onClick={copyPgn}
        disabled={currentMove == null}
        title="Copy this move's PGN"
      >
        <FontAwesomeIcon icon={pgnCopied ? faCheck : faClipboard} />
      </button>
      {isMobile && !inline ? (
        <button
          type="button"
          className="cm-comment-panel-close"
          onClick={closePanel}
          title="Close"
          aria-label="Close comments"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      ) : null}
    </span>
  );

  const commentCount = scoped.filter((c) => c.Status !== 'Removed').length;
  const commentsBlock = (
    <div className="cm-comment-panel-body">
      {currentMove != null ? (
        <>
          <h4 className="cm-cp-comments-title">
            <FontAwesomeIcon icon={faComment} className="me-2" />
            Your notes{commentCount > 0 ? ` (${commentCount})` : ''}
          </h4>
          <CommentTreeView
            key={movePgn ?? 'game'}
            gameId={game.Id}
            comments={scoped}
            scopeMovePgn={movePgn}
            emptyText="Nothing noted on this move yet."
          />
        </>
      ) : (
        <div className="text-muted small">Select a move to see and add your notes.</div>
      )}
    </div>
  );

  return (
    <div className={`cm-comment-panel${inline ? ' cm-comment-panel--inline' : ''}`}>
      <div
        className="cm-comment-panel-head"
        onTouchStart={isMobile && !inline ? onTouchStart : undefined}
        onTouchEnd={isMobile && !inline ? onTouchEnd : undefined}
      >
        {isMobile && !inline ? (
          <span className="cm-comment-panel-grabber" aria-hidden="true" />
        ) : null}
        <span className="cm-comment-panel-title">{moveHead}</span>
        {moveActions}
      </div>

      {/* Everything below the pinned header scrolls together. */}
      <div className="cm-comment-panel-scroll">
        {inline ? (
          // Mobile view: the real board is directly above, so the body is note -> comments -> alternatives.
          <>
            {motifBannerEl != null ||
            commentaryBlock != null ||
            riskBlock != null ||
            difficultyBlock != null ||
            bestLineBlock != null ? (
              <div className="cm-cp-analysis">
                {motifBannerEl}
                {commentaryBlock}
                {riskBlock}
                {difficultyBlock}
                {repairMoveBtn}
                {bestLineBlock}
              </div>
            ) : null}
            {commentsBlock}
            {moveTagBlock}
            {alternativesBlock}
          </>
        ) : (
          <>
            {currentMove != null ? (
              <div className="cm-cp-analysis">
                {/* Mobile bottom sheet only: a board, since the sheet covers the real one. */}
                {isMobile ? (
                  <>
                    <GamesListItemEvaluationBar />
                    <div
                      className="cm-cp-board-row"
                      onTouchStart={onBoardTouchStart}
                      onTouchEnd={onBoardTouchEnd}
                    >
                      <MiniChessboard
                        fen={currentMove.Fen}
                        pgn={currentMove.Pgn}
                        orientation={orientation}
                        moveType={currentMove.MoveType}
                        parentBestMove={parentBestMove}
                        coordinates
                      />
                    </div>
                    <GamesListItemControls keyboardNav={false} />
                  </>
                ) : null}
                {motifBannerEl}
                {commentaryBlock}
                {riskBlock}
                {difficultyBlock}
                {repairMoveBtn}
                {bestLineBlock}
              </div>
            ) : null}
            {commentsBlock}
            {moveTagBlock}
            {alternativesBlock}
          </>
        )}
      </div>
    </div>
  );
}
